import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, retry, switchMap } from 'rxjs';

import { ImpersonableUser, TestUser, TopologyIntegrationService } from '@mobi/rwc-utils-azure-ng-jslib';
import { ConfigurationService, Logger, SKIP_JAP_HEADER_CONTEXT } from '@mobi/rwc-utils-common-ng-jslib';

import { AuthenticationService, UserData } from '../../authentication.service';

const toImpersonableUser: (u: TestUser) => TestImpersonableUser = u => ({
    userId: u.userId,
    label: u.anzeigeName,
    testUser: true
});

const impersonableUserComparator: (a: { userId: string }, b: { userId: string }) => number = (a, b) =>
    a.userId.localeCompare(b.userId);

@Injectable({ providedIn: 'root' })
export class CipTestUsersService {
    #configurationService = inject(ConfigurationService);
    #httpClient = inject(HttpClient);
    #logger = inject(Logger).createCustomLogger('Impersonation');
    #topologyIntegrationService = inject(TopologyIntegrationService);
    #impersonationBaseUrl$ = this.#topologyIntegrationService.topology$.pipe(
        switchMap(topology =>
            this.#httpClient.get<{ mobi_impersonations_endpoint: string }>(
                topology.business_idp_metadata_uri,
                SKIP_JAP_HEADER_CONTEXT
            )
        ),
        map(idpMetadata => idpMetadata.mobi_impersonations_endpoint)
    );

    readonly testUsers$: Observable<TestImpersonableUser[]> = inject(AuthenticationService)
        .getUserData()
        .pipe(switchMap(user => this.#getImpersonationMapping(user)));

    #getImpersonationMapping(user: UserData): Observable<TestImpersonableUser[]> {
        if (user.impersonator?.username) {
            this.#logger.debug(`Test user '${user.preferred_username}' cannot impersonate, skip fetch allowed list.`);
            return of([]);
        }
        const infraPreset = this.#configurationService.getSync('infraPreset');
        const mappedInfraPreset = infraPreset === 'b2eadmin' ? 'b2e' : infraPreset;

        return this.#impersonationBaseUrl$.pipe(
            map(baseUrl => `${baseUrl}/${mappedInfraPreset}/${user.email}`),
            switchMap(url => this.#httpClient.get<ImpersonationMappingTestuserResponse>(url, SKIP_JAP_HEADER_CONTEXT)),
            map(response => response.testusers.map(toImpersonableUser).sort(impersonableUserComparator)),
            retry({ count: 3, delay: 300 }),
            catchError(exception => {
                this.#logger.error(`Error while fetching impersonation via url from topology endpoint.`, { exception });
                return of([]);
            })
        );
    }
}

interface ImpersonationMappingTestuserResponse {
    upn: string;
    testusers: TestUser[];
}

type TestImpersonableUser = Omit<ImpersonableUser, 'testUser' | 'cipId'> & {
    testUser: true;
    cipId?: never;
};
